/*
 * Copyright (c) 2026 SimplyBook.me Ltd. All rights reserved.
 * Licensed under the Apache License, Version 2.0.
 */

import React, { useCallback, createContext, useContext, useState, useMemo, useEffect } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { useClientLogin } from "../lib/react-query/useClientLogin";
import { useClientLogout } from "../lib/react-query/useClientLogout";
import { useClientRegister } from "../lib/react-query/useClientRegister";
import { useClientUpdate } from "../lib/react-query/useClientUpdate";
import { useClientDetails } from "../lib/react-query/useClientDetails";
import { useTokenStorage } from "./useTokenStorage";
import { usePostClientRemindPassword } from "../lib/react-query/usePostClientRemindPassword";
import { useClientLoginByHash } from "../lib/react-query/useClientLoginByHash";
import { queryKeys, clientScopedQueryKeys } from "../lib/react-query/queryKeys";
import { invalidateClientTerms } from "../lib/react-query/invalidateClientTerms";
import { AUTH_EVENTS } from "../constants/constants";

const AuthContext = createContext(null);

const removeClientScopedQueries = (queryClient) => {
  clientScopedQueryKeys.forEach((queryKey) => queryClient.removeQueries({ queryKey }));
};

export const AuthProvider = ({ children }) => {
  const queryClient = useQueryClient();
  const { setToken, clearTokens, token, rememberMe } = useTokenStorage();
  const [authError, setAuthError] = useState(null);
  const [isInitialized, setIsInitialized] = useState(false);

  const {
    data: client,
    isLoading: isClientLoading,
    refetch: fetchClientDetails,
  } = useClientDetails({
    enabled: !!token && isInitialized,
  });

  const { mutateAsync: loginMutate, isPending: isLoginPending } = useClientLogin();
  const { mutateAsync: logoutMutate, isPending: isLogoutPending } = useClientLogout();
  const { mutateAsync: registerMutate, isPending: isRegisterPending } = useClientRegister();
  const { mutateAsync: editMutate, isPending: isEditPending } = useClientUpdate();
  const { mutateAsync: remindPasswordMutate, isPending: isRemindPasswordPending } = usePostClientRemindPassword();
  const { mutateAsync: loginByHashMutate, isPending: isLoginByHashPending } = useClientLoginByHash();

  // Listen for session expiry events from axios interceptor
  useEffect(() => {
    const handleSessionExpired = () => {
      // Show toast only if user was logged in
      if (window.toastManager && queryClient.getQueryData(queryKeys.client.details)) {
        window.toastManager.addToast({
          title: "Session Expired",
          description: "Your session has expired.",
          variant: "warning",
          id: Date.now(),
        });
      }

      clearTokens();
      removeClientScopedQueries(queryClient);
    };

    window.addEventListener(AUTH_EVENTS.SESSION_EXPIRED, handleSessionExpired);
    
    return () => window.removeEventListener(AUTH_EVENTS.SESSION_EXPIRED, handleSessionExpired);
  }, [clearTokens, queryClient]);

  const isAuthenticated = !!token && !!client;

  const clearError = useCallback(() => {
    setAuthError(null);
  }, []);

  const handleAuthSuccess = useCallback(
    async (authToken, shouldRemember = false) => {
      setAuthError(null);
      setToken({ token: authToken, rememberMe: shouldRemember, isAnonymous: false });
      
      removeClientScopedQueries(queryClient);
      await queryClient.invalidateQueries({ queryKey: queryKeys.client.details });
    },
    [setToken, queryClient]
  );

  const handleAuthError = useCallback((error) => {
    const errorMessage = error?.response?.data?.message || error?.message;
    setAuthError(errorMessage);

    throw error;
  }, []);

  const login = useCallback(
    async ({ email, password, remember_me }) => {
      try {
        clearError();
        const response = await loginMutate({ login: email, password });
        await handleAuthSuccess(response.token, remember_me);

        return response;
      } catch (error) {
        handleAuthError(error);
      }
    },
    [loginMutate, handleAuthSuccess, handleAuthError, clearError]
  );

  const register = useCallback(
    async (userData) => {
      try {
        clearError();
        const { cancellation_terms, promotion_letters, simplybook_terms, user_terms, privacy_policy, ...restData } =
          userData;

        const clientData = {
          ...restData,
          terms: { cancellation_terms, promotion_letters, simplybook_terms, user_terms, privacy_policy },
        };

        const response = await registerMutate(clientData);
        await handleAuthSuccess(response.token, false);

        return { ...response, acceptedTerms: clientData.terms };
      } catch (error) {
        handleAuthError(error);
      }
    },
    [registerMutate, handleAuthSuccess, handleAuthError, clearError]
  );

  const logout = useCallback(async () => {
    try {
      clearError();
      const clientLogout = await logoutMutate();

      clearTokens();

      removeClientScopedQueries(queryClient);
      queryClient.removeQueries({ queryKey: queryKeys.token.all });

      if (clientLogout?.token) {
        setToken({ token: clientLogout.token, rememberMe: false, isAnonymous: true });
      }

      return clientLogout;
    } catch (error) {
      const errorMessage = error?.response?.data?.message || error?.message || "Logout failed";
      setAuthError(errorMessage);
      throw error;
    }
  }, [logoutMutate, queryClient, clearTokens, setToken, clearError]);

  const edit = useCallback(
    async (userData) => {
      try {
        clearError();
        const response = await editMutate(userData);

        queryClient.setQueryData(queryKeys.client.details, response);
        // is_email_unsubscribed/is_sms_unsubscribed can flip accepted_promotion_letters server-side
        // (either channel unsubscribed clears it), so the cached terms query must not go stale here
        invalidateClientTerms(queryClient, response);

        return response;
      } catch (error) {
        queryClient.invalidateQueries({ queryKey: queryKeys.client.details });
        handleAuthError(error);
      }
    },
    [editMutate, queryClient, handleAuthError, clearError]
  );

  const remindPassword = useCallback(
    async ({ email }) => {
      try {
        clearError();
        return await remindPasswordMutate({ email });
      } catch (error) {
        handleAuthError(error);
      }
    },
    [clearError, handleAuthError, remindPasswordMutate]
  );

  const loginByHash = useCallback(
    async ({ hash }) => {
      try {
        clearError();
        const response = await loginByHashMutate({ hash });
        await handleAuthSuccess(response.token, false);

        return response;
      } catch (error) {
        handleAuthError(error);
      }
    },
    [clearError, handleAuthError, loginByHashMutate, handleAuthSuccess]
  );

  const contextValue = useMemo(
    () => ({
      // State
      token,
      client,
      isAuthenticated,
      error: authError,
      isInitialized,
      rememberMe,

      // Loading states (from React Query)
      isLoginPending,
      isLogoutPending,
      isRegisterPending,
      isEditPending,
      isClientLoading,
      isRemindPasswordPending,
      isLoginByHashPending,

      // Actions
      login,
      register,
      logout,
      edit,
      remindPassword,
      loginByHash,
      fetchClientDetails,
      clearError,
      setIsInitialized,
    }),
    [
      token,
      client,
      isAuthenticated,
      authError,
      isInitialized,
      rememberMe,
      isLoginPending,
      isLogoutPending,
      isRegisterPending,
      isEditPending,
      isClientLoading,
      isRemindPasswordPending,
      isLoginByHashPending,
      login,
      register,
      logout,
      edit,
      remindPassword,
      loginByHash,
      fetchClientDetails,
      clearError,
      setIsInitialized,
    ]
  );

  return <AuthContext.Provider value={contextValue}>{children}</AuthContext.Provider>;
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error("useAuth must be used within an AuthProvider");
  }
  return context;
};
